import type { RoleCode, Session } from '../api/contrat.js';

/**
 * Rôles, espaces et layouts de la coquille. L’espace décide du layout et de la lisibilité (UX-20) :
 * l’équipe travaille en 16 px avec Poppins, les externes et le portail en 18 px avec la police système.
 */
export type Espace = 'equipe' | 'externe' | 'portail';
export type Layout = 'equipe-bureau' | 'equipe-mobile' | 'externe' | 'portail';

export const ESPACES: Readonly<Record<RoleCode, { espace: Espace; libelleCourt: string }>> = {
  admin: { espace: 'equipe', libelleCourt: 'administration' },
  dirigeante: { espace: 'equipe', libelleCourt: 'dirigeante' },
  assistante: { espace: 'equipe', libelleCourt: 'assistante' },
  agent: { espace: 'equipe', libelleCourt: 'agent' },
  clerc: { espace: 'externe', libelleCourt: 'clerc de notaire' },
  notaire: { espace: 'externe', libelleCourt: 'notaire' },
  prestataire: { espace: 'externe', libelleCourt: 'prestataire' },
  mandant: { espace: 'portail', libelleCourt: 'mandant' },
  heritier: { espace: 'portail', libelleCourt: 'héritier' },
  representant_legal: { espace: 'portail', libelleCourt: 'représentant légal' },
  contact_delegue: { espace: 'portail', libelleCourt: 'contact délégué' },
};

/** Les huit rôles qui ont des écrans dans les maquettes. */
export const ROLES_COQUILLE = ['admin', 'dirigeante', 'assistante', 'agent', 'clerc', 'notaire', 'mandant', 'heritier'] as const;

/** Largeur à partir de laquelle l’équipe passe en layout bureau. */
export const LARGEUR_BUREAU = 900;

export const libelleCourtRole = (role: RoleCode): string => ESPACES[role].libelleCourt;

/** Rôle dont la personne voit l’application : la personne incarnée pendant un « voir en tant que ». */
export function roleEffectif(session: Session): RoleCode {
  const utilisateur = session.effectiveUser ?? session.user;
  return utilisateur.roles[0]?.role ?? 'heritier';
}

export function choisirLayout(role: RoleCode, largeur: number): Layout {
  const { espace } = ESPACES[role];
  if (espace === 'equipe') return largeur >= LARGEUR_BUREAU ? 'equipe-bureau' : 'equipe-mobile';
  return espace;
}

const ACCUEIL: Readonly<Record<RoleCode, string>> = {
  admin: '/administration',
  dirigeante: '/pilotage',
  assistante: '/file-du-jour',
  agent: '/fiche-rapide',
  clerc: '/espace-clerc',
  notaire: '/espace-notaire',
  prestataire: '/espace-prestataire',
  mandant: '/portail/mandant',
  heritier: '/portail/progression',
  representant_legal: '/portail/progression',
  contact_delegue: '/portail/progression',
};

const ACCUEIL_MOBILE: Partial<Readonly<Record<RoleCode, string>>> = {
  dirigeante: '/decisions',
  assistante: '/appel',
  agent: '/fiche-rapide',
};

export function accueilPour(role: RoleCode, largeur: number): string {
  if (ESPACES[role].espace === 'equipe' && largeur < LARGEUR_BUREAU) return ACCUEIL_MOBILE[role] ?? ACCUEIL[role];
  return ACCUEIL[role];
}
